// A finance modal's frame since DD-051: the shared Drawer, open while it is mounted. The page mounts it for the URL's
// `modal` param and unmounts it on close, so every flow keeps its URL and its history (DD-045, useFinanceParams). A
// drawer that mounts while another is still mounted is a swap in one commit: a hand-over, or a modal's data replacing
// its spinner. It appears in place, without the slide, so the step reads as the same drawer moving on. A `stacked` drawer
// (a create flow started inside a drawer, one level only: DD-045) opens on top of the one still mounted: it slides in and
// adds no backdrop, so the page darkens as with one drawer, and a click outside or its X closes only it. Closed, it gives
// focus back to the button that opened it (the walk, 2026-10-23: it was left on the page's body), for every `stack=`
// flow: Radix's own close step focuses the dialog's Trigger, and these drawers, opened from the URL, have none. Open, every
// finance drawer puts focus on its X, whatever its header holds (the sweep, 2026-10-23: the client drawer focused the
// dialog, the job and invoice drawers the X), unless a field inside took it as it mounted (a payment's amount). Every one
// is one width, md: a drawer reads the same whichever list opened it.
import { useLayoutEffect, useState, type ReactNode } from "react";
import { Drawer } from "@/components/ui/Drawer";

/** The finance drawers mounted now: one, or two while one is stacked on another (DD-045). */
let mounted = 0;

/** Desktop widths. On a phone every drawer is the full-width bottom sheet. Finance's own drawers keep md. */
const WIDTH = { md: "md:max-w-lg", lg: "md:max-w-2xl", xl: "md:max-w-4xl" } as const;

export type FinanceDrawerSize = keyof typeof WIDTH;

interface Props {
  /** The X, Escape and a click outside. closeUnlessPending holds it while a request is in flight. */
  onClose: () => void;
  /** "Verb · subject" (DD-056): the heading, and the drawer's name. */
  title: string;
  /** Beside the heading: a way to the item's own page, as an arrow (ArrowUpRightIcon, user comment, 2026-10-16). */
  titleAside?: ReactNode;
  footer?: ReactNode;
  /** md: a form, the task drawer's width. lg: a split or a statement. xl: the contract and one-off line editors. */
  size?: FinanceDrawerSize;
  /** On top of a drawer still open: it slides in, with no backdrop of its own. */
  stacked?: boolean;
  children: ReactNode;
}

export function FinanceDrawer({ onClose, title, titleAside, footer, size = "md", stacked = false, children }: Props) {
  // Read in the first render, before the commit unmounts the drawer this one replaces.
  const [slides] = useState(() => stacked || mounted === 0);
  // The element focused as it mounts: the button inside the drawer under it that opened it.
  const [opener] = useState(() => (stacked && document.activeElement instanceof HTMLElement ? document.activeElement : null));
  useLayoutEffect(() => {
    mounted += 1;
    return () => {
      mounted -= 1;
    };
  }, []);
  return (
    <Drawer
      isOpen
      onClose={onClose}
      title={title}
      titleAside={titleAside}
      footer={footer}
      bodyPadding
      animateIn={slides}
      overlay={!stacked}
      className={WIDTH[size]}
      onOpenAutoFocus={(event) => {
        const content = event.currentTarget;
        const close = content instanceof HTMLElement ? content.querySelector<HTMLElement>('button[aria-label="Close"]') : null;
        if (!close) return;
        event.preventDefault();
        close.focus();
      }}
      onCloseAutoFocus={
        opener
          ? (event) => {
              event.preventDefault();
              if (opener.isConnected) opener.focus();
            }
          : undefined
      }
    >
      {children}
    </Drawer>
  );
}
